<template>
  <client-only>
    <div v-for="position in positions"
         :key="position"
         class="toasts mt-4 pointer-events-none flex justify-center items-center w-full"
         :class="[position]">
      <transition-group name="fade-from-bottom"
                        tag="div"
                        class="inline-flex gap-3 flex-col"
                        :class="widthAlignmentClass(position)">
        <toast v-for="toast in toastsByPosition(position)"
               :key="toast.id"
               :toast="toast"
               @remove-toast="removeToast"
               @pause-timeout="pauseTimeout"
               @resume-timeout="resumeTimeout"/>
      </transition-group>
    </div>
  </client-only>
</template>

<script setup lang="ts">
import Toast from "~/components/global/toasts/Toast.vue";
import {useToastStore} from "~/store/toast";

const {toasts, removeToast} = useToastStore();
const positions = ['top', 'top-left', 'top-right', 'bottom', 'bottom-left', 'bottom-center', 'bottom-right'];

const toastsByPosition = (position: string) => {
  return computed(() => toasts.filter((toast) => toast.position === position)).value;
};

// Align toasts based on their position
const widthAlignmentClass = (position: string) => {
  if (position.includes('left')) {
    return 'items-start';
  } else if (position.includes('right')) {
    return 'items-end';
  } else {
    return 'items-center';
  }
};

type ToastTimer = {
  timer: ReturnType<typeof setTimeout> | null;
  start: number;
  remaining: number;
};

const toastTimers = reactive<Record<string, ToastTimer>>({});

// Pause timeout and update remaining duration in the store
const pauseTimeout = (toastId: string, setDuration = false) => {
  const toastTimer = toastTimers[toastId];
  const toast = toasts.find((toast) => toast.id === toastId);

  if (toastTimer && toast) {
    if (toastTimer.timer) {
      clearTimeout(toastTimer.timer);
      toastTimer.timer = null;
    }
    const elapsed = Date.now() - toastTimer.start;
    toastTimer.remaining -= elapsed;
    toastTimer.remaining = Math.max(0, toastTimer.remaining);
    // Update the remaining duration in the store
    toast.remaining = toastTimer.remaining;

    if (setDuration)
      toast.duration = toastTimer.remaining;
  }
};

// Resume timeout using the remaining duration from the store
const resumeTimeout = (toastId: string) => {
  const toastTimer = toastTimers[toastId];
  const toast = toasts.find((toast) => toast.id === toastId);
  if (toastTimer && toast) {
    if (toastTimer.timer) {
      clearTimeout(toastTimer.timer);
      toastTimer.timer = null;
    }
    toastTimer.start = Date.now();
    toastTimer.remaining = toast.remaining;
    toastTimer.timer = setTimeout(() => {
      removeToast(toastId, false);
      delete toastTimers[toastId];
    }, toastTimer.remaining);
  }
};

watch(toasts, (newToasts) => {
  // Remove timers for toasts that no longer exist
  Object.keys(toastTimers).forEach((id) => {
    if (!newToasts.find((toast) => toast.id === id)) {
      const timer = toastTimers[id]?.timer;
      if (timer) clearTimeout(timer);
      delete toastTimers[id];
    }
  });

  // Handle timers for new toasts
  newToasts.forEach((toast) => {
    if (toast.duration === 0) return; // Skip infinite duration toasts

    if (!toastTimers[toast.id]) {
      // Create a new timer for the toast
      toastTimers[toast.id] = {
        timer: null,
        start: Date.now(),
        remaining: toast.remaining,
      };

      // Start the timer
      toastTimers[toast.id].timer = setTimeout(() => {
        removeToast(toast.id, false);
        delete toastTimers[toast.id];
      }, toast.remaining);
    }
  });
},
{deep: false, immediate: false}
);

onMounted(() => {
  // Recreate timers for existing toasts in the store
  toasts.forEach((toast) => {
    if (toast.duration === 0) return; // Skip infinite duration toasts

    if (!toastTimers[toast.id]) {
      toastTimers[toast.id] = {
        timer: null,
        start: Date.now(),
        remaining: toast.remaining,
      };
    }

    if (toast.remaining > 0) {
      resumeTimeout(toast.id);
    } else {
      // Remove toast if duration has expired
      removeToast(toast.id, false);
    }
  });
});

// Cleanup on unmount
onBeforeUnmount(() => {
  toasts.forEach((toast) => {
    pauseTimeout(toast.id, true);
  });
});
</script>

<style>
.toasts {
  position: fixed;
  z-index: 1050;
}

/* Transition classes for "toast" transition-group */
.toast-enter-active, .toast-leave-active {
  transition: opacity 0.2s;
}

.toast-enter-from, .toast-leave-to {
  opacity: 0;
}

.toast-enter-to, .toast-leave-from {
  opacity: 1;
}

.top-left {
  top: 0;
  left: 0;
}

.top-right {
  top: 0;
  right: 0;
}

.top {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

.bottom-left {
  bottom: 3rem;
  left: 0;
}

.bottom-center {
  bottom: 3rem;
  left: 50%;
  transform: translateX(-50%);
}

.bottom-right {
  bottom: 3rem;
  right: 0;
}

.bottom {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

@keyframes progress {
  from {
    width: 100%;
  }
  to {
    width: 0;
  }
}

.animate-progress {
  animation: progress linear forwards;
}

.pause-animation {
  animation-play-state: paused;
}
</style>